import { useState, type FunctionComponent } from "react";
import { CheckOutlined, CloseOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Empty, Image as AntImage, Popconfirm, Space, Table, Tag, Typography, message } from "antd";

import {
  ChallengeCompletionStatus,
  type ChallengeTaskCompletionEntity,
  type ChallengeVerification,
} from "@fitness-app/data-models/entities/Challenge";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import { challengeVerificationLabels } from "./constants";
import { formatChallengeDate } from "./helpers";

type ApprovalRow = ChallengeTaskCompletionEntity & {
  trainee: Pick<Trainee, "firstName" | "lastName" | "email"> | null;
  task: { title: string; points: number; verification: ChallengeVerification } | null;
};

type Props = {
  challengeId: string;
};

const ChallengeApprovalQueue: FunctionComponent<Props> = ({ challengeId }) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const queryKey = ["challenge-approval-queue", challengeId];

  const queueQuery = useQuery({
    queryKey,
    // Poll while open + refetch on focus so new proof submissions appear without a manual refresh.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_task_completion")
        .select(
          "*, trainee:traineeId!inner(firstName, lastName, email), task:taskId(title, points, verification)",
        )
        .eq("challengeId", challengeId)
        .eq("status", ChallengeCompletionStatus.Pending)
        .order("date", { ascending: false })
        .returns<ApprovalRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const decisionMutation = useMutation({
    mutationFn: async ({ row, approve }: { row: ApprovalRow; approve: boolean }) => {
      const { error } = await supabase
        .from("challenge_task_completion")
        .update({
          status: approve ? ChallengeCompletionStatus.Approved : ChallengeCompletionStatus.Rejected,
          pointsAwarded: approve ? row.task?.points ?? 0 : 0,
          verifiedAt: new Date().toISOString(),
          verifiedBy: memberId ?? null,
        } as never)
        .eq("id", row.id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: async (_data, { approve }) => {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ["challenge-leaderboard", challengeId] });
      void message.success(approve ? "Zadanie zaakceptowane" : "Zadanie odrzucone");
    },
    onError: (error: Error) => {
      void message.error(`Operacja nie powiodła się: ${error.message}`);
    },
  });

  if (!queueQuery.isFetching && (queueQuery.data?.length ?? 0) === 0) {
    return <Empty description="Brak zadań do akceptacji" />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Table<ApprovalRow>
        dataSource={queueQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={queueQuery.isFetching}
        locale={{ emptyText: "Brak zadań do akceptacji" }}
        pagination={false}
      >
        <Table.Column<ApprovalRow>
          title="Klientka"
          dataIndex="trainee"
          key="trainee"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>
                {row.trainee?.firstName} {row.trainee?.lastName}
              </Typography.Text>
              <Typography.Text type="secondary">{row.trainee?.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<ApprovalRow>
          title="Zadanie"
          dataIndex="task"
          key="task"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text>{row.task?.title ?? "-"}</Typography.Text>
              <Space size={4} wrap>
                <Tag color="blue">{row.task?.points ?? 0} pkt</Tag>
                {row.task?.verification ? (
                  <Tag>{challengeVerificationLabels[row.task.verification] ?? row.task.verification}</Tag>
                ) : null}
              </Space>
            </Space>
          )}
        />
        <Table.Column<ApprovalRow>
          title="Data"
          dataIndex="date"
          key="date"
          render={(date: string) => formatChallengeDate(date)}
        />
        <Table.Column<ApprovalRow>
          title="Dowód"
          dataIndex="proofUrl"
          key="proofUrl"
          render={(proofUrl: string | null) => {
            if (!proofUrl) {
              return <Typography.Text type="secondary">Brak</Typography.Text>;
            }
            // Photo proofs (pedometer screenshots etc.) preview inline; videos open the player.
            if (/\.(jpe?g|png|webp|heic|heif|gif)(\?|$)/i.test(proofUrl)) {
              return (
                <AntImage
                  src={proofUrl}
                  width={56}
                  height={56}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                  alt="Dowód wykonania"
                />
              );
            }
            return (
              <Button icon={<PlayCircleOutlined />} onClick={() => setPlayingUrl(proofUrl)}>
                Obejrzyj wideo
              </Button>
            );
          }}
        />
        <Table.Column<ApprovalRow>
          title="Notatka"
          dataIndex="note"
          key="note"
          render={(note: string | null) => note || <Typography.Text type="secondary">-</Typography.Text>}
        />
        <Table.Column<ApprovalRow>
          title="Akcje"
          key="actions"
          render={(_, row) => (
            <Space>
              <Popconfirm
                title="Zaakceptować to zadanie?"
                okText="Akceptuj"
                cancelText="Anuluj"
                onConfirm={() => decisionMutation.mutate({ row, approve: true })}
              >
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={decisionMutation.isLoading && decisionMutation.variables?.row.id === row.id}
                >
                  Akceptuj
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Odrzucić to zadanie?"
                okText="Odrzuć"
                okButtonProps={{ danger: true }}
                cancelText="Anuluj"
                onConfirm={() => decisionMutation.mutate({ row, approve: false })}
              >
                <Button
                  danger
                  icon={<CloseOutlined />}
                  loading={decisionMutation.isLoading && decisionMutation.variables?.row.id === row.id}
                >
                  Odrzuć
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
      <VideoPlayer videoUrl={playingUrl} visible={Boolean(playingUrl)} onClose={() => setPlayingUrl(null)} />
    </Space>
  );
};

export default ChallengeApprovalQueue;
